// src/App.tsx
import React, { useEffect, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { useDispatch } from "react-redux";
//import "./App.css";
import AppRoutes from "./routes";

// ── Auth rehydration actions ──────────────────────────────────────
// Both slices persist to localStorage on login and need to be read
// back on every app startup. Without dispatching these, Redux starts
// with empty state on every refresh even though the session is valid.
import { loadFromStorage } from "./app/features/authSlice";
import { loadAdminFromStorage } from "./app/features/adminAuthSlice";

/* ─────────────────────────────────────────────────────────────────
   AUTH HYDRATION GATE

   React renders before useEffect fires, so without a gate:
     1. Redux state is empty (hydrated: false)
     2. AppRoutes renders — protected routes see no token → redirect to /login
     3. useEffect fires, loadFromStorage restores the session
     4. User is now on /login even though they had a valid session

   The fix: don't render AppRoutes until BOTH slices have been read
   from localStorage. This takes < 1ms (synchronous localStorage reads)
   so the blank screen is imperceptible.
───────────────────────────────────────────────────────────────── */

function AuthLoader({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // These are synchronous reducers — localStorage is read immediately.
    // We dispatch both before marking the app as ready so no route can
    // render with an empty auth state.
    dispatch(loadFromStorage());
    dispatch(loadAdminFromStorage());
    setReady(true);
  }, [dispatch]);

  if (!ready) {
    // Minimal loading shell — avoids layout shift and protects routes.
    // Replace with your own splash/skeleton if desired.
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "#f1f3f8",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
          }}
        >
          {/* FSL brand mark */}
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 12,
              background: "#1b2d5e",
              color: "white",
              fontWeight: 700,
              fontSize: 18,
            }}
          >
            F
          </span>
          {/* Subtle spinner */}
          <div
            style={{
              width: 20,
              height: 20,
              border: "2px solid #dde2ec",
              borderTopColor: "#1b2d5e",
              borderRadius: "50%",
              animation: "spin 0.7s linear infinite",
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

/* ─────────────────────────────────────────────────────────────────
   APP ROOT
───────────────────────────────────────────────────────────────── */

function App() {
  return (
    <BrowserRouter>
      {/*
        AuthLoader must be INSIDE BrowserRouter (so Link/Navigate work)
        but wraps AppRoutes so no route renders until auth is hydrated.
      */}
      <AuthLoader>
        <AppRoutes />
      </AuthLoader>
    </BrowserRouter>
  );
}

export default App;
